import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/api';

const DEFAULT_CUSTOM_RANGE = { start: '', end: '' };

export function useCrashReportData(selectedDevice, selectedInterval, customRange = DEFAULT_CUSTOM_RANGE) {
    const [reports, setReports] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchReports = useCallback(async () => {
        if (!selectedDevice || !selectedInterval) {
            setReports([]);
            setMeta(null);
            setLastUpdated(null);
            return;
        }

        if (selectedInterval === 'custom' && (!customRange?.start || !customRange?.end)) {
            setReports([]);
            setMeta(null);
            setLastUpdated(null);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const extraParams =
                selectedInterval === 'custom' && customRange?.start && customRange?.end
                    ? { dt_start: customRange.start, dt_end: customRange.end }
                    : undefined;

            const data = await apiClient.getCrashReports(selectedDevice, selectedInterval, extraParams);
            const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];

            setReports(items);
            setMeta(data?.meta ?? null);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Error fetching crash reports:', err);
            if (err?.status === 403) {
                setError('You do not have permission to view crash reports.');
            } else if (err?.status === 401) {
                setError('Session expired. Please sign in again.');
            } else {
                setError(err?.message || 'Unknown error fetching crash reports.');
            }
            setReports([]);
            setMeta(null);
            setLastUpdated(null);
        } finally {
            setLoading(false);
        }
    }, [selectedDevice, selectedInterval, customRange?.start, customRange?.end]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    return {
        reports,
        meta,
        loading,
        error,
        lastUpdated,
        refresh: fetchReports,
    };
}
